import * as React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import WbCloudyIcon from '@mui/icons-material/WbCloudy';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import { formatDate } from '../../../../../shared/LanguageService';
import { LocationData } from '../queries/useLocationQuery';
import { forecastsForTheDay } from '../state/weather';

type WeatherSummaryProps = {
    locationData: LocationData;
    bookingDate: Date;
};

const box = {
    display: 'flex',
    alignItems: 'center',
    height: '68px',
    padding: '0 16px',
    gap: '24px'
};

const smallText = { fontSize: '11px', lineHeight: 1.66, letterSpacing: '0.4px' };

function TimeOfDay({ icon, lines }: { icon: React.ReactNode; lines: string[] }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {icon}
            {lines.map((line) => (
                <Typography key={line} sx={smallText}>
                    {line}
                </Typography>
            ))}
        </Box>
    );
}

export function WeatherSummary({ locationData, bookingDate }: WeatherSummaryProps) {
    let forecasts = forecastsForTheDay(locationData.forecasts, bookingDate);

    return (
        <Box sx={{ display: 'flex', gap: '12px' }}>
            {forecasts.length > 0 && (
                <Paper variant="outlined" sx={box}>
                    {forecasts.map((forecast) => (
                        <TimeOfDay
                            key={forecast.label}
                            icon={
                                forecast.isSunny ? (
                                    <WbSunnyIcon
                                        sx={{ fontSize: '15px', color: 'warning.light' }}
                                    />
                                ) : (
                                    <WbCloudyIcon
                                        sx={{ fontSize: '15px', color: 'primary.main' }}
                                    />
                                )
                            }
                            lines={[`${forecast.precipitation} mm`, forecast.label]}
                        />
                    ))}
                </Paper>
            )}
            <Paper variant="outlined" sx={{ ...box, gap: '16px' }}>
                <TimeOfDay
                    icon={<WbSunnyIcon sx={{ fontSize: '29px', color: 'warning.light' }} />}
                    lines={[formatDate(new Date(locationData.sunrise), 'HH:mm')]}
                />
                <Divider orientation="vertical" flexItem sx={{ margin: '10px 0' }} />
                <TimeOfDay
                    icon={<WbTwilightIcon sx={{ fontSize: '29px', color: 'warning.main' }} />}
                    lines={[formatDate(new Date(locationData.sunset), 'HH:mm')]}
                />
            </Paper>
        </Box>
    );
}
